import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Film, Heading, Link, Image, Bold, Palette, Type, WrapText, ArrowUp, ArrowDown, Plus, Trash2 } from 'lucide-react';
import { HeroConfig } from '../helpers/contentHelpers';
import { Button } from './ui/button';
import ImageUploader from './ImageUploader';

interface HeroConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentConfig: HeroConfig;
  onSave: (config: HeroConfig) => Promise<boolean>;
}

const FONT_SIZES = [
  { label: 'Pequeno', value: '14px' },
  { label: 'Médio', value: '18px' },
  { label: 'Grande', value: '24px' },
  { label: 'Enorme', value: '32px' },
];

const PRESET_COLORS = [
  '#FFFFFF', '#0FFF55', '#FFD700', '#FF6B6B', '#60A5FA',
  '#F472B6', '#A78BFA', '#FB923C', '#34D399', '#F87171',
];

const HeroConfigModal: React.FC<HeroConfigModalProps> = ({
  isOpen,
  onClose,
  currentConfig,
  onSave
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [tools, setTools] = useState<HeroConfig['tools']>([]);
  const [mastershopButton, setMastershopButton] = useState<{ name: string; href: string }>({ name: 'MASTERSHOP', href: '#' });
  const [isSaving, setIsSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeFontSize, setActiveFontSize] = useState('18px');
  const editorRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && currentConfig) {
      setVideoUrl(currentConfig.video_url);
      setTools(JSON.parse(JSON.stringify(currentConfig.tools)));
      if (currentConfig.mastershop_button) {
        setMastershopButton({ ...currentConfig.mastershop_button });
      } else {
        setMastershopButton({ name: 'MASTERSHOP', href: '#' });
      }
      // Set initial headline HTML
      if (editorRef.current) {
        editorRef.current.innerHTML = currentConfig.headline || 'HEADLINE ECOSSISTEMA MASTERSHOP';
      }
    }
  }, [isOpen, currentConfig]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setShowColorPicker(false);
      }
    };
    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  if (!isOpen) return null;

  const execFormat = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const handleBold = () => execFormat('bold');

  const handleColor = (color: string) => {
    execFormat('foreColor', color);
    setShowColorPicker(false);
  };

  const handleFontSize = (size: string) => {
    // execCommand fontSize only accepts 1-7, so we use a workaround with inline styles
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      // No selection — apply font size to new content via a wrapper span
      const span = document.createElement('span');
      span.style.fontSize = size;
      range.insertNode(span);
      // Move cursor inside span
      range.setStart(span, 0);
      range.setEnd(span, 0);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Wrap selected text in a span
      const selectedContent = range.extractContents();
      const span = document.createElement('span');
      span.style.fontSize = size;
      span.appendChild(selectedContent);
      range.insertNode(span);
    }
    setActiveFontSize(size);
  };

  const handleToolChange = (index: number, field: 'name' | 'image_url' | 'href', value: string) => {
    const updatedTools = [...tools];
    updatedTools[index] = { ...updatedTools[index], [field]: value };
    setTools(updatedTools);
  };

  const handleAddTool = () => {
    setTools([...tools, { name: '', href: '', image_url: '' }]);
  };

  const handleRemoveTool = (index: number) => {
    const updated = tools.filter((_, i) => i !== index);
    setTools(updated);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...tools];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;
    setTools(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === tools.length - 1) return;
    const updated = [...tools];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;
    setTools(updated);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsSaving(true);
    try {
      const headlineHtml = editorRef.current?.innerHTML || currentConfig.headline;
      const success = await onSave({ headline: headlineHtml, video_url: videoUrl, tools, mastershop_button: mastershopButton });
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar configurações da Hero:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Box */}
      <div className="relative bg-[#0f1114] border border-[#1f2329] hover:border-[#0FFF55]/30 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transition-all duration-300">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1f2329] bg-[#121316]">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Film className="w-5 h-5 text-[#0FFF55]" />
            Editar Configurações da Hero
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-[#1a1d23] rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* ── Headline Rich Text Editor ── */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Heading className="w-4 h-4 text-gray-400" />
              Headline do Ecossistema
            </label>

            {/* Toolbar */}
            <div className="flex items-center gap-1 bg-[#1a1d23] border border-[#1f2329] rounded-xl px-3 py-2 flex-wrap">
              {/* Bold */}
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleBold(); }}
                className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-[#2a2d33] transition-colors font-bold"
                title="Negrito"
              >
                <Bold className="w-4 h-4" />
              </button>

              <div className="w-px h-5 bg-[#1f2329] mx-1" />

              {/* Font Size Dropdown */}
              <div className="flex items-center gap-1">
                <Type className="w-3.5 h-3.5 text-gray-400" />
                <select
                  className="bg-[#121316] border border-[#1f2329] text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-[#0FFF55]"
                  value={activeFontSize}
                  onChange={(e) => handleFontSize(e.target.value)}
                >
                  {FONT_SIZES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              <div className="w-px h-5 bg-[#1f2329] mx-1" />

              {/* Color Picker */}
              <div className="relative" ref={colorPickerRef}>
                <button
                  type="button"
                  onClick={() => setShowColorPicker((p) => !p)}
                  className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-[#2a2d33] transition-colors flex items-center gap-1.5"
                  title="Cor do texto"
                >
                  <Palette className="w-4 h-4" />
                  <span className="text-xs hidden sm:inline">Cor</span>
                </button>

                {showColorPicker && (
                  <div className="absolute top-full left-0 mt-2 bg-[#1a1d23] border border-[#1f2329] rounded-xl p-3 shadow-2xl z-10 w-52">
                    <p className="text-xs text-gray-400 mb-2">Selecione a cor:</p>
                    <div className="grid grid-cols-5 gap-2 mb-3">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); handleColor(color); }}
                          className="w-8 h-8 rounded-full border-2 border-transparent hover:border-white transition-all hover:scale-110"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-400">Custom:</label>
                      <input
                        type="color"
                        className="w-8 h-8 rounded cursor-pointer bg-transparent border-none"
                        onChange={(e) => handleColor(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="w-px h-5 bg-[#1f2329] mx-1" />

              {/* Line Break Button */}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  editorRef.current?.focus();
                  document.execCommand('insertHTML', false, '<br>&#8203;');
                }}
                className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-[#2a2d33] transition-colors flex items-center gap-1.5"
                title="Pular linha"
              >
                <WrapText className="w-4 h-4" />
                <span className="text-xs hidden sm:inline">Pular linha</span>
              </button>
            </div>

            {/* ContentEditable Editor */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              className="w-full min-h-[56px] bg-[#121316] border border-[#1f2329] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55] transition-all text-sm tracking-widest uppercase font-black"
              style={{ wordBreak: 'break-word' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Insert <br> + zero-width space on Enter so cursor can move to new line
                  document.execCommand('insertHTML', false, '<br>&#8203;');
                }
              }}
            />

            <p className="text-xs text-gray-500">
              Selecione o texto para aplicar formatações. Suporta cores, negrito e tamanho de fonte.
            </p>
          </div>

          {/* ── Video URL ── */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Film className="w-4 h-4 text-gray-400" />
              URL do Vídeo VSL (YouTube, Vimeo ou Link Direto)
            </label>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="w-full bg-[#121316] border border-[#1f2329] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55] transition-all text-sm"
              placeholder="Ex: https://www.youtube.com/watch?v=..."
              required
            />
          </div>

          {/* ── Botão Principal MASTERSHOP ── */}
          <div className="space-y-4 bg-[#121316] border border-[#1f2329] rounded-xl p-4 hover:border-[#0FFF55]/20 transition-all">
            <h4 className="text-sm font-bold text-white border-b border-[#1f2329]/60 pb-2 flex items-center gap-2">
              <Link className="w-4 h-4 text-[#0FFF55]" />
              Configuração do Botão MASTERSHOP
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs text-gray-400 font-medium">Nome do Botão</label>
                <input
                  type="text"
                  value={mastershopButton.name}
                  onChange={(e) => setMastershopButton(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-[#0f1114] border border-[#1f2329] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0FFF55] transition-all text-xs"
                  placeholder="Ex: MASTERSHOP"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-gray-400 font-medium">Link de Redirecionamento</label>
                <input
                  type="text"
                  value={mastershopButton.href}
                  onChange={(e) => setMastershopButton(prev => ({ ...prev, href: e.target.value }))}
                  className="w-full bg-[#0f1114] border border-[#1f2329] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0FFF55] transition-all text-xs"
                  placeholder="https://..."
                  required
                />
              </div>
            </div>
          </div>

          {/* ── Tools Grid ── */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-[#1f2329] pb-2">
              <h4 className="text-sm font-bold text-white">
                Ícones e Links do Ecossistema ({tools.length} Botões)
              </h4>
              <button
                type="button"
                onClick={handleAddTool}
                className="flex items-center space-x-1 bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/30 hover:bg-[#0FFF55]/20 px-3 py-1.5 rounded-xl text-xs font-bold transition-all"
              >
                <Plus size={14} />
                <span>Adicionar Botão</span>
              </button>
            </div>

            {tools.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-[#1f2329] rounded-2xl bg-[#121316]">
                <p className="text-sm text-gray-500">Nenhum botão cadastrado no ecossistema.</p>
                <button
                  type="button"
                  onClick={handleAddTool}
                  className="mt-3 inline-flex items-center space-x-1.5 bg-[#0FFF55] text-black hover:bg-[#0dd149] px-4 py-2 rounded-xl text-xs font-bold transition-all"
                >
                  <Plus size={14} />
                  <span>Adicionar Primeiro Botão</span>
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {tools.map((tool, index) => (
                  <div
                    key={index}
                    className="bg-[#121316] border border-[#1f2329] rounded-xl p-4 space-y-4 hover:border-[#0FFF55]/20 transition-all"
                  >
                    <div className="flex items-center justify-between border-b border-[#1f2329]/60 pb-2">
                      <span className="text-xs font-bold text-[#0FFF55] tracking-wider uppercase">
                        Ferramenta {index + 1}: {tool.name || `Botão ${index + 1}`}
                      </span>
                      <div className="flex items-center space-x-1">
                        {/* Move Up */}
                        <button
                          type="button"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none hover:bg-[#1a1d23] rounded-lg transition-all"
                          title="Mover para Cima"
                        >
                          <ArrowUp size={14} />
                        </button>
                        {/* Move Down */}
                        <button
                          type="button"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === tools.length - 1}
                          className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:pointer-events-none hover:bg-[#1a1d23] rounded-lg transition-all"
                          title="Mover para Baixo"
                        >
                          <ArrowDown size={14} />
                        </button>
                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => handleRemoveTool(index)}
                          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all ml-2"
                          title="Excluir Botão"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Tool Name */}
                      <div className="space-y-1.5">
                        <label className="text-xs text-gray-400 font-medium">Nome do Botão</label>
                        <input
                          type="text"
                          value={tool.name}
                          onChange={(e) => handleToolChange(index, 'name', e.target.value)}
                          className="w-full bg-[#0f1114] border border-[#1f2329] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0FFF55] transition-all text-xs"
                          placeholder="Ex: YOUTUBE"
                          required
                        />
                      </div>

                      {/* Tool Link */}
                      <div className="space-y-1.5">
                        <label className="text-xs text-gray-400 font-medium flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          Link de Redirecionamento
                        </label>
                        <input
                          type="text"
                          value={tool.href}
                          onChange={(e) => handleToolChange(index, 'href', e.target.value)}
                          className="w-full bg-[#0f1114] border border-[#1f2329] rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0FFF55] transition-all text-xs"
                          placeholder="https://..."
                          required
                        />
                      </div>

                      {/* Image Uploader */}
                      <div className="md:col-span-2">
                        <ImageUploader
                          bucketName="ferramentas"
                          label="Logo/Imagem da Ferramenta"
                          value={tool.image_url}
                          onChange={(url) => handleToolChange(index, 'image_url', url)}
                          placeholder="Insira o link ou selecione arquivo..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-[#1f2329] bg-[#121316] flex items-center justify-end gap-3">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="border-[#1f2329] bg-[#121316] hover:bg-[#1a1d23] text-gray-300 hover:text-white"
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleSubmit()}
            className="bg-[#0FFF55] hover:bg-[#0dd149] text-black font-bold flex items-center gap-2"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar Configurações
              </>
            )}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default HeroConfigModal;
